import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { scanSound } from '../scanSound';
import { productsText, useMyLang } from './productsText';
import { Column, Expected, Lookup, ScanRow, FIELD_ORDER, PER_SIZE_ONLY, expectedFor, lookup, patchRow, detailOf, rightFor, post, myMoves } from './scanApi';
import StorageTierCard from './StorageTierCard';

// The product page — layer 2 of Products (Fernando 31.10: "the first layer is the products in this table … click on
// that variant and that opens this product's page: now you only see this product, all its variants and the shops where
// it is, and you can edit it"). Opened from a row of the Products table. One call (/api/products/scan?wms_id=) brings
// the style in its shop and its twins in the other shops; a card per size, the clicked one framed; a tap opens the
// WMS-editable fields (what is editable comes from the server's column catalogue, the same list the table is built
// from); "How many are on the shelf?" = the Inventory rule; "Move this product" asks which shelf is being emptied and
// opens the move page. Light palette like the floor screens. -- Claude, 2026-10-31

const input = 'bg-white border border-[#9aa0a6] text-[#111111] rounded px-2 py-1.5 text-base';
const btn = 'px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-40';

export default function ProductPage() {
  const { wmsId = '' } = useParams<{ wmsId: string }>();
  const navigate = useNavigate();
  const lang = useMyLang();
  const t = productsText(lang);
  const [res, setRes] = useState<Lookup | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [exp, setExp] = useState<Record<string, Expected>>({});
  const [expBusy, setExpBusy] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const [notice, setNotice] = useState('');
  const [movePick, setMovePick] = useState(false);
  const [fromCode, setFromCode] = useState('');
  const [alsoFrom, setAlsoFrom] = useState<Set<string>>(new Set());
  const [openMoves, setOpenMoves] = useState<{ id: number; name: string | null; style_key: string; from_display: string | null }[]>([]);
  const [moveHit, setMoveHit] = useState<ScanRow | null>(null);     // the pool row the shelf chooser was built from
  const [fetching, setFetching] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await lookup('', { wms_id: Number(wmsId) });
      setRes(r);
    } catch (e: any) { setErr(e?.message || 'Not found'); }
  }, [wmsId]);
  useEffect(() => { load(); myMoves().then(setOpenMoves).catch(() => {}); }, [load]);

  const rows: ScanRow[] = useMemo(() => (res?.groups || []).flatMap(g => g.sizes), [res]);
  const shopName = (id: string) => res?.shops?.[id]?.name || id;
  const editable: Column[] = useMemo(() => (res?.columns || []).filter(c => c.editable), [res]);
  const hitRow = rows.find(r => r.wms_id === Number(wmsId));
  const rights = res?.rights;
  const canEdit = !!rights?.can_edit;
  // the storage tier lives on the WGR / Shopify style — a Big Oliver row asks with its Motley twin's id
  const tierWmsId = useMemo(() => {
    const own = (res?.groups || []).filter(g => g.platform !== 'mcf');
    const hitGroup = own.find(g => g.sizes.some(s => s.wms_id === Number(wmsId)));
    return hitGroup ? Number(wmsId) : own[0]?.sizes[0]?.wms_id ?? null;
  }, [res, wmsId]);

  const loadExpected = async () => {
    setExpBusy(true); setErr('');
    try { setExp(await expectedFor(rows.map(r => r.wms_id))); }
    catch (e: any) { setErr(e?.message || 'Something went wrong'); }
    finally { setExpBusy(false); }
  };

  /** one field of one row, the same PATCH the Products table makes; the row on screen follows the server's answer */
  const saveField = async (row: ScanRow, field: string, value: string): Promise<boolean> => {
    const r = await patchRow(row.wms_id, field, value);
    if (!r.ok) { setNotice(`${t.notSaved}: ${await detailOf(r)}`); scanSound('error'); return false; }
    const d = await r.json();
    // a shelf edit moves the other shop's row of the same piece too (Motley ↔ Big Oliver, 5.11) — it follows here
    const twins: { wms_id: number; webshop_id: string; field?: string; value?: string | null; error?: string }[] = d.twins || [];
    const twinOf = (x: ScanRow) => twins.filter(tw => tw.wms_id === x.wms_id && tw.field)
      .reduce((acc, tw) => ({ ...acc, [tw.field as string]: tw.value }), {} as Record<string, any>);
    setRes(prev => prev ? { ...prev, groups: (prev.groups || []).map(g => ({ ...g, sizes: g.sizes.map(x => x.wms_id === row.wms_id ? { ...x, ...d.product } : { ...x, ...twinOf(x) }) })) } : prev);
    const q = (d.queued || [])[0];
    const col = editable.find(c => c.key === field);
    const moved = Array.from(new Set(twins.filter(tw => !tw.error).map(tw => shopName(tw.webshop_id))));
    const failed = Array.from(new Set(twins.filter(tw => tw.error).map(tw => shopName(tw.webshop_id))));
    setNotice([q ? t.sending(col?.label || field, shopName(q.webshop_id)) : t.saved,
               moved.length ? t.twinFollows(moved.join(' + ')) : '', failed.length ? t.twinFailed(failed.join(' + ')) : '']
      .filter(Boolean).join(' · '));
    return true;
  };

  /** the move: which shelf is being emptied (the sizes of the pool shop grouped by shelf) */
  const poolRows = res?.status === 'ok' ? rows.filter(r => r.webshop_id === res.pool_shop) : [];
  const shelves = useMemo(() => {
    const m = new Map<string, ScanRow[]>();
    for (const r of poolRows) { const k = r.stock_location_norm || ''; m.set(k, [...(m.get(k) || []), r]); }
    return Array.from(m.entries()).map(([code, rs]) => ({ code, display: rs[0].stock_location || t.noShelf, rows: rs }));
  }, [poolRows, t.noShelf]);
  const openMovePick = () => {
    const h = hitRow && hitRow.webshop_id === res?.pool_shop ? hitRow : poolRows.find(r => r.barcode && r.barcode === hitRow?.barcode) || poolRows[0];
    setMoveHit(h || null);
    setFromCode(h?.stock_location_norm || ''); setAlsoFrom(new Set()); setMovePick(true); setOpen(null);
  };
  const startMove = async () => {
    // the move starts from the pool shop's row (a Big Oliver size with no Motley twin cannot start one — 159306-770, 31.10)
    const base = moveHit || hitRow;
    if (!res || !base) return;
    setBusy(true); setErr('');
    try {
      const r = await post('/move/start', { wms_id: base.wms_id, from_code: fromCode, also_from: Array.from(alsoFrom), code: res.code || String(base.wms_id) });
      const j = await r.json().catch(() => ({} as any));
      if (!r.ok) {
        const d = j.detail;
        if (d && typeof d === 'object' && d.code === 'move_open') { navigate(`/products/move/${d.move_id}`); return; }
        setErr(typeof d === 'string' ? d : d?.message || `HTTP ${r.status}`); scanSound('error'); return;
      }
      navigate(`/products/move/${j.move.id}`);
    } finally { setBusy(false); }
  };
  /** every size of a Motley Denim style from the shop, now (the nightly sync fetches known styles whole since 31.10) */
  const poolPlatform = res?.groups?.find(g => g.webshop_id === res.pool_shop)?.platform;
  const fetchStyle = async () => {
    const base = poolRows[0] || hitRow;
    if (!base) return;
    setFetching(true); setErr('');
    try {
      const r = await post('/fetch-style', { wms_id: base.wms_id });
      if (!r.ok) { setErr(await detailOf(r)); scanSound('error'); return; }
      const j = await r.json();
      setNotice(t.fetchAllDone((j.new || []).length, j.fetched));
      await load();
    } finally { setFetching(false); }
  };

  if (!res && !err) return <div className="min-h-screen bg-[#e5e7eb] p-6 text-[#5f6368]">…</div>;

  return (
    <div className="min-h-screen bg-[#e5e7eb] text-[#111111]"><div className="p-3 sm:p-6 max-w-3xl">
      <button onClick={() => navigate('/products')} className="text-[#6b7280] text-sm hover:text-[#111111]">{t.backToTable}</button>
      {err && <div className="mt-3 px-3 py-2 rounded-lg bg-red-700 text-white text-sm font-semibold">{err}</div>}
      {notice && <div className="mt-3 px-3 py-2 rounded-lg bg-white border border-[#d1d5db] text-sm flex justify-between gap-3"><span>{notice}</span><button onClick={() => setNotice('')} className="text-[#6b7280]">✕</button></div>}
      {openMoves.length > 0 && (
        <div className="mt-3 px-3 py-2 rounded-lg bg-amber-100 border border-amber-700 text-sm flex flex-wrap items-center gap-2">
          <span>{t.moveResume}</span>
          {openMoves.map(o => (
            <button key={o.id} onClick={() => navigate(`/products/move/${o.id}`)} className="underline font-medium">{o.name || o.style_key}{o.from_display ? ` (${o.from_display})` : ''}</button>
          ))}
        </div>
      )}
      {rights?.device && <div className="mt-3 text-sm text-[#5f6368]">{t.station}</div>}
      {rights && !rights.device && !canEdit && <div className="mt-3 text-sm text-[#5f6368]">{t.noRights}</div>}

      {res?.status === 'ok' && (
        <div className="mt-3">
          <div className="bg-white rounded-xl border border-[#d1d5db] p-3">
            <div className="text-xl font-bold leading-snug">{res.name || res.style_key}</div>
            <div className="text-xs text-[#5f6368] mt-0.5">{res.style_key} · {rows.length} {t.sizes} · {(res.groups || []).map(g => shopName(g.webshop_id)).join(', ')}</div>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <button onClick={loadExpected} disabled={expBusy || !rows.length} className={`${btn} bg-[#dfe3e8] hover:bg-[#c6cdd5]`}>{expBusy ? t.expectedLoading : t.expectedBtn}</button>
              {rights?.stock_location && hitRow && !movePick && (
                <button onClick={openMovePick} className={`${btn} bg-green-700 hover:bg-green-600 text-white`}>{t.move}</button>
              )}
              {rights?.products_edit && poolPlatform === 'wgr' && (
                <button onClick={fetchStyle} disabled={fetching} className={`${btn} bg-[#dfe3e8] hover:bg-[#c6cdd5]`}>{fetching ? t.fetchAllBusy : t.fetchAll}</button>
              )}
            </div>
            {(res.groups || []).some(g => g.truncated) && <div className="text-xs text-amber-700 mt-2">{t.truncated}</div>}
            {movePick && (
              <div className="mt-3 border-t border-[#d1d5db] pt-3">
                <div className="font-semibold">{t.moveFrom}</div>
                <div className="text-xs text-[#6b7280] mb-2">{t.moveFromHint}</div>
                <div className="flex flex-wrap gap-2">
                  {shelves.map(s => (
                    <label key={s.code} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${fromCode === s.code ? 'bg-blue-50 border-blue-500' : 'bg-white border-[#d1d5db]'}`}>
                      <input type="radio" name="from" checked={fromCode === s.code} onChange={() => { setFromCode(s.code); setAlsoFrom(a => { const n = new Set(a); n.delete(s.code); return n; }); }} />
                      <span className="font-mono font-semibold">{s.display}</span>
                      <span className="text-xs text-[#6b7280]">{s.rows.map(r => r.size).join(', ')}</span>
                    </label>
                  ))}
                </div>
                {shelves.length > 1 && (
                  <div className="mt-3">
                    <div className="text-sm">{t.moveAlsoFrom}</div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {shelves.filter(s => s.code !== fromCode).map(s => (
                        <label key={s.code} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${alsoFrom.has(s.code) ? 'bg-blue-50 border-blue-500' : 'bg-white border-[#d1d5db]'}`}>
                          <input type="checkbox" checked={alsoFrom.has(s.code)} onChange={e => setAlsoFrom(a => { const n = new Set(a); if (e.target.checked) n.add(s.code); else n.delete(s.code); return n; })} />
                          <span className="font-mono">{s.display}</span>
                          <span className="text-xs text-[#6b7280]">{s.rows.map(r => r.size).join(', ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  <button disabled={busy} onClick={startMove} className={`${btn} bg-green-700 hover:bg-green-600 text-white`}>{t.moveStart}</button>
                  <button onClick={() => setMovePick(false)} className="px-3 py-2 text-[#5f6368] text-sm underline">{t.close}</button>
                </div>
              </div>
            )}
          </div>

          {!rights?.device && <StorageTierCard wmsId={tierWmsId} />}

          {(res.groups || []).map(g => (
            <div key={`${g.webshop_id}:${g.style_key}`} className="mt-3">
              <div className="text-xs uppercase tracking-wider text-[#5f6368] px-1 mb-1">{shopName(g.webshop_id)} · {g.style_key}</div>
              <div className="space-y-2">
                {g.sizes.map(r => (
                  <RowCard key={r.wms_id} row={r} exp={exp[String(r.wms_id)]} t={t} shop={shopName(r.webshop_id)} isOpen={open === r.wms_id}
                    hit={r.wms_id === Number(wmsId)} canEdit={canEdit} onToggle={() => setOpen(open === r.wms_id ? null : r.wms_id)}
                    form={open === r.wms_id && canEdit ? (
                      <EditForm row={r} columns={editable} rights={rights!} t={t} shown={rows} shopName={shopName} save={saveField} />
                    ) : null} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div></div>
  );
}

function RowCard({ row, exp, t, shop, isOpen, hit, canEdit, onToggle, form }: {
  row: ScanRow; exp?: Expected; t: ReturnType<typeof productsText>; shop: string; isOpen: boolean; hit: boolean; canEdit: boolean; onToggle: () => void; form: React.ReactNode;
}) {
  return (
    <div className={`bg-white rounded-xl border ${hit ? 'border-blue-500' : 'border-[#d1d5db]'} p-3`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-xl font-bold">{row.size || t.empty} <span className="text-sm font-normal text-[#5f6368]">{row.label_size && row.label_size !== row.size ? `(${row.label_size})` : ''}</span></div>
        <div className="text-xs text-[#5f6368]">{shop}</div>
      </div>
      <div className="text-xs text-[#5f6368]">{[row.article_number, row.picklist_number].filter(Boolean).join(' · ')}</div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <span>{t.location} <b className="font-mono text-blue-700">{row.stock_location || t.empty}</b></span>
        {row.restock_location && <span>{t.restock} <span className="font-mono">{row.restock_location}</span></span>}
        <span>{t.stock} <b>{row.stock_qty ?? t.empty}</b></span>
      </div>
      {exp && <ExpectedLine e={exp} t={t} />}
      <div className="mt-1 text-xs text-[#5f6368] font-mono">{row.barcode || t.empty}{row.aliases?.length ? ` · ${t.aliases} ${row.aliases.join(', ')}` : ''}</div>
      {canEdit && (
        <div className="mt-2"><button onClick={onToggle} className="text-sm text-blue-700 underline">{isOpen ? t.close : t.edit}</button></div>
      )}
      {form}
    </div>
  );
}

function ExpectedLine({ e, t }: { e: Expected; t: ReturnType<typeof productsText> }) {
  if (e.infinite) return <div className="mt-1 text-sm text-[#5f6368]">{t.expected}: {t.nos}</div>;
  return (
    <div className="mt-1 text-sm">
      <span className="font-semibold">{t.onShelf(e.expected)}</span>
      <span className="text-xs text-[#5f6368]"> · {e.live} {e.source === 'live' ? t.live : t.synced}{e.unpicked > 0 ? ` + ${t.soldNotPicked(e.unpicked)}` : ''}</span>
      {e.mirror_of && <span className="text-xs text-[#5f6368]"> · {t.mirror}</span>}
      {e.note === 'no_twin' && <span className="text-xs text-[#5f6368]"> · {t.noTwin}</span>}
      {e.in_flight > 0 && <div className="mt-1 text-xs bg-amber-100 border border-amber-700 rounded px-2 py-1 text-amber-900">{t.pickerOnWay(e.in_flight)}</div>}
    </div>
  );
}

function EditForm({ row, columns, rights, t, shown, shopName, save }: {
  row: ScanRow; columns: Column[]; rights: { stock_location: boolean; products_edit: boolean }; t: ReturnType<typeof productsText>;
  shown: ScanRow[]; shopName: (id: string) => string; save: (row: ScanRow, field: string, value: string) => Promise<boolean>;
}) {
  const [more, setMore] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const first = FIELD_ORDER.map(k => columns.find(c => c.key === k)).filter((c): c is Column => !!c);
  const rest = columns.filter(c => !FIELD_ORDER.includes(c.key));
  const list = more ? [...first, ...rest] : first;
  const val = (c: Column) => drafts[c.key] ?? (row[c.key] === null || row[c.key] === undefined ? '' : String(row[c.key]));
  const changed = (c: Column) => drafts[c.key] !== undefined && drafts[c.key] !== (row[c.key] === null || row[c.key] === undefined ? '' : String(row[c.key]));

  const commit = async (c: Column, all = false) => {
    if (!changed(c) && !all) return;
    setSaving(c.key);
    const v = val(c);
    let ok = await save(row, c.key, v);
    if (ok && all) {
      for (const other of shown) {
        if (other.wms_id === row.wms_id) continue;
        if (String(other[c.key] ?? '') === v) continue;
        ok = (await save(other, c.key, v)) && ok;
      }
    }
    setSaving(null);
    if (ok) setDrafts(d => { const n = { ...d }; delete n[c.key]; return n; });
  };

  return (
    <div className="mt-3 border-t border-[#d1d5db] pt-3 space-y-3">
      {list.map(c => {
        const may = rights[rightFor(c.key)];
        const sync = c.syncs?.[row.webshop_id];
        const push = row.push_status?.[c.key];
        const mark = push ? (push.status === 'ok' ? <span className="text-green-700" title={push.at || ''}>✓</span>
          : push.status === 'failed' || push.status === 'unsupported' ? <span className="text-red-700" title={push.error || ''}>✗</span>
          : <span className="text-[#6b7280]" title={push.status}>⟳</span>) : null;
        return (
          <div key={c.key}>
            <div className="flex items-baseline justify-between gap-2">
              <label className="text-xs text-[#5f6368]">{c.label} {mark}</label>
              <span className="text-[11px] text-[#6b7280]">
                {sync ? (sync.on ? <span className="text-emerald-700">↔ {t.syncsTo} {sync.store}: {sync.field}</span> : <span>↔ {sync.store} off</span>) : <span>{t.wmsOnly}</span>}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 items-center mt-0.5">
              {c.type === 'bool' ? (
                <select value={val(c)} disabled={!may || saving === c.key} onChange={e => setDrafts({ ...drafts, [c.key]: e.target.value })}
                  onBlur={() => commit(c)} className={`${input} w-28`}>
                  <option value="">{t.empty}</option><option value="true">{t.yes}</option><option value="false">{t.no}</option>
                </select>
              ) : (
                <input value={val(c)} disabled={!may || saving === c.key} inputMode={c.type === 'int' || c.type === 'num' ? 'decimal' : 'text'}
                  onChange={e => setDrafts({ ...drafts, [c.key]: e.target.value })} onBlur={() => commit(c)}
                  onKeyDown={e => { if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); } }}
                  className={`${input} w-full sm:w-72 ${c.key === 'stock_location' || c.key === 'barcode' ? 'font-mono' : ''}`} />
              )}
              {changed(c) && <button disabled={saving === c.key} onClick={() => commit(c)} className={`${btn} bg-blue-600 hover:bg-blue-500 text-white`}>{saving === c.key ? t.saving : t.save}</button>}
              {may && !PER_SIZE_ONLY.has(c.key) && shown.length > 1 && (changed(c) || saving === c.key) && (
                <button disabled={saving === c.key} onClick={() => commit(c, true)} className={`${btn} bg-[#dfe3e8] hover:bg-[#c6cdd5]`}>{t.applyAll(shown.length)}</button>
              )}
            </div>
          </div>
        );
      })}
      {rest.length > 0 && <button onClick={() => setMore(m => !m)} className="text-sm text-blue-700 underline">{more ? t.fewerFields : t.moreFields}</button>}
    </div>
  );
}
